import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import {
  NbButtonModule,
  NbCardModule,
  NbDateService,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { InsightStatusHandlerComponent } from 'src/app/features/insights/insights-table/view-insight/insight-status-handler/insight-status-handler.component';
import { ReloadInsights } from 'src/app/features/insights/redux';
import { InsightsState } from 'src/app/features/insights/redux/insights.state';
import { InsightService } from 'src/app/features/insights/services/insight.service';
import { Campaign } from 'src/app/models/campaign';
import { FREQUENCIES } from 'src/app/models/frequencies';
import { Insight } from 'src/app/models/insight';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { RouterState } from '../../../../auth/redux';
import { ManageCampaign, PostSaveCampaignComplete } from '../../../redux';
import { CampaignService } from '../../../services/campaign.service';
/*
This component acts as a handler for activating and deactivating campagins
*/

@Component({
  selector: 'app-campaign-status-handler',
  templateUrl: './campaign-status-handler.component.html',
  styleUrls: ['./campaign-status-handler.component.css'],
  imports: [
    NbCardModule,
    NgIf,
    NbIconModule,
    NbButtonModule,
    NbInputModule,
    FormsModule,
    OwlDateTimeModule,
    NbSelectModule,
    NgFor,
    NbOptionModule,
    NbTooltipModule
  ]
})
export class CampaignStatusHandlerComponent implements OnInit, OnDestroy {
  public insight$: Observable<any[]> = inject(Store).select(InsightsState.allInsights);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public insights: any[] = [];
  public username = '';
  public action = '';
  public campaign!: Campaign;
  public FREQUENCIES = FREQUENCIES;
  public startDate?: Date;
  public endDate?: Date;
  public minStartDate: Date;
  public minEndDate: Date;

  constructor(
    protected ref: NbDialogRef<CampaignStatusHandlerComponent>,
    private service: CampaignService,
    private insightService: InsightService,
    private action$: Actions,
    private toastr: NbToastrService,
    private dialogService: NbDialogService,
    protected dateService: NbDateService<Date>
  ) {
    this.minStartDate = this.dateService.addMonth(this.dateService.today(), 0);
    this.minEndDate = this.dateService.addDay(this.dateService.today(), 1);
  }

  public ngOnInit() {
    this.insight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.insights = data));
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.action$.pipe(ofActionCompleted(ReloadInsights), takeUntil(this.unsubscribe)).subscribe(() => {
      this.insight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.insights = data));
    });
    this.action$
      .pipe(ofActionCompleted(PostSaveCampaignComplete), takeUntil(this.unsubscribe))
      .subscribe(() => this.close());
    this.action$.pipe(ofActionCompleted(ManageCampaign), takeUntil(this.unsubscribe)).subscribe(() => this.close());

    if (this.campaign.start_date) {
      this.startDate = new Date(this.campaign.start_date * 1000);
      this.minEndDate = this.dateService.addDay(this.startDate, 1);
    }
    if (this.campaign.end_date) {
      this.endDate = new Date(this.campaign.end_date * 1000);
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public activate() {
    if (!this.campaign.start_date || !this.campaign.end_date) {
      this.toastr.danger('', $localize`Start and end times have to be provided to activate the Campaign.`);
      return;
    }
    const insight: Insight = this.insights.find(({ id }) => id === this.campaign.trigger_id);
    if (insight) {
      if (insight.enabled && insight.start_date && insight.start_date < this.campaign.start_date) {
        this.campaign.enabled = true;
        this.campaign.next_run_time = this.campaign.start_date;
        this.campaign.last_updated_by = this.username;
        this.service.postCampaignsAmplify(this.campaign, undefined, true);
      } else {
        this.dialogService.open(InsightStatusHandlerComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: { insight: Insight.copy(insight), action: ACTIONS.ACTIVATE }
        });
        this.toastr.danger(
          '',
          $localize`Associated Insight should be active and have start date before the start date of the Campaign.`
        );
      }
    } else {
      this.toastr.danger('', $localize`Associated Insight not found.`);
    }
  }

  public deactivate() {
    this.campaign.last_updated_by = this.username;
    this.campaign.enabled = false;
    this.campaign.start_date = undefined;
    this.campaign.end_date = undefined;
    this.campaign.schedule_seconds = undefined;
    this.campaign.next_run_time = undefined;
    this.service.postCampaignsAmplify(this.campaign, undefined, false);

    const insight: Insight = this.insights.find(({ id }) => id === this.campaign.trigger_id);
    if (insight) {
      const updatedInsight = Insight.copy(insight);
      updatedInsight.last_updated_by = this.username;
      updatedInsight.enabled = false;
      updatedInsight.start_date = undefined;
      updatedInsight.end_date = undefined;
      updatedInsight.schedule_seconds = undefined;
      updatedInsight.next_run_time = undefined;
      this.insightService.postInsightsAmplify(updatedInsight, false);
    }
  }

  public close() {
    this.ref.close();
  }

  public changeEvent() {
    if (this.startDate) {
      this.campaign.start_date = Math.round(this.startDate.getTime() / 1000);
      if (this.endDate) {
        this.campaign.end_date = Math.round(this.endDate.getTime() / 1000);
        if (this.startDate.getTime() > this.endDate.getTime()) {
          this.endDate = this.dateService.addDay(new Date(this.startDate.getTime()), 1);
        }
      } else {
        this.campaign.end_date = undefined;
      }
      this.minEndDate = this.dateService.addDay(new Date(this.startDate.getTime()), 1);
    } else {
      this.campaign.start_date = undefined;
    }
  }
}
